import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormGroup } from '@angular/forms';
import { Subject } from 'rxjs';
import { distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { EditorBookingItemEditStore } from '../editor-booking-item-edit/editor-booking-item-edit.store';
import { EditorBookingItemMetadata } from '../editor-booking-item-metadata/editor-booking-item-metadata.model';
import { InheritableType } from '../is-inheritable/is-inheritable.model';
import { getInheritableTypeFromList } from '../is-inheritable/utils';


@Component({
    selector: 'eas-editor-booking-item-metadata-list-form',
    templateUrl: './editor-booking-item-metadata-list-form.component.html',
    styleUrls: ['./editor-booking-item-metadata-list-form.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditorBookingItemMetadataListFormComponent implements OnInit, OnDestroy {
  @Input() parentFormGroup: FormGroup;
  @Input() itemMetadataList: EditorBookingItemMetadata[];
  @Input() parentItemMetadataList: EditorBookingItemMetadata[];
  @Input() itemMetadataListFormArray: FormArray;
  @Input() newItemMetadataListFormArray: FormArray;

  private destroyed$ = new Subject<void>();

  listInheritableType: InheritableType;

  constructor(private itemEditStore: EditorBookingItemEditStore) {

  }

  ngOnInit() {
    this.reloadListInheritableType();

    const inheritableTypeAsEditedList = this.itemMetadataList.map(metadata => metadata.getInheritableTypeAsEdited());

    this.itemMetadataListFormArray.valueChanges.pipe(
      takeUntil(this.destroyed$),
      map(_ => this.itemMetadataListFormArray.controls.map(itemMetadataForm => itemMetadataForm.dirty)),
      distinctUntilChanged()
    ).subscribe(
      (dirtyList) => {
        dirtyList.forEach((dirty, index) => {
          if (dirty) {
            this.itemMetadataList[index].inheritableType = inheritableTypeAsEditedList[index];
          }
        });

        this.reloadListInheritableType();
      }
    );

    this.newItemMetadataListFormArray.valueChanges.pipe(
      takeUntil(this.destroyed$),
      map(_ => this.itemMetadataListFormArray.controls.map(itemMetadataForm => itemMetadataForm.dirty)),
      distinctUntilChanged()
    ).subscribe((_) => this.reloadListInheritableType());
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  reloadListInheritableType() {
    this.listInheritableType = getInheritableTypeFromList(
      this.itemMetadataList.map(metadata => metadata.inheritableType).concat(
        this.newItemMetadataListFormArray.controls
          .filter(newItemMetadataListForm => newItemMetadataListForm.dirty)
          .map(_ => 'set' as InheritableType)
      )
    );
  }

  onAdd() {
    this.newItemMetadataListFormArray.push(this.itemEditStore.buildItemNewMetadataForm());
    return false;
  }

  onDelete(index: number) {
    const metadataForm = this.getMetadataFormGroup(index);

    if (this.parentItemMetadataList === null) {
      metadataForm.setValue({ value: null });
      this.itemMetadataList[index].inheritableType = 'empty';
    } else {
      const parentItemMetadata = this.parentItemMetadataList.find(
        metadata => metadata.name === this.itemMetadataList[index].name
      );
      if (parentItemMetadata === undefined) {
        metadataForm.setValue({ value: null });
        this.itemMetadataList[index].inheritableType = 'empty';
      } else {
        metadataForm.setValue({ value: parentItemMetadata.value });
        this.itemMetadataList[index].inheritableType = parentItemMetadata.getClonedInheritableTypeAsChild();
      }
    }

    metadataForm.markAsPristine();
    metadataForm.updateValueAndValidity();

    return false;
  }

  isDeletable(index: number) {
    return this.getMetadataFormGroup(index).enabled
      && this.itemMetadataList[index].isDeletable();
  }

  onDeleteNewMetadata(index: number) {
    this.newItemMetadataListFormArray.removeAt(index);
    return false;
  }

  getTitle(): string {
    return $localize `Metadata`;
  }

  getMetadataFormGroup(index): FormGroup {
    return this.itemMetadataListFormArray.at(index) as FormGroup;
  }

  getNewMetadataFormGroup(index): FormGroup {
    return this.newItemMetadataListFormArray.at(index) as FormGroup;
  }
}
